import { type FormEvent, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Button, Card, ErrorNote, PageHeader, Spinner, formatDateTime } from "../components/ui"
import { api } from "../lib/api"
import { useSession } from "../lib/auth"

// Approving a terminal.
//
// The employee gets here from `moderniser providers login`, which prints a link
// carrying the code. The code is shown anyway, and prominently: the only thing
// standing between this page and someone approving a stranger's session is that
// they can see the same characters on their own screen.

type DeviceRequest = {
  userCode: string
  label: string | null
  status: string
  expiresAt: string
  createdAt: string
}

export function DevicePage() {
  const [params] = useSearchParams()
  const { employee, company } = useSession()

  const [code, setCode] = useState(params.get("code")?.toUpperCase() ?? "")
  const [request, setRequest] = useState<DeviceRequest | null>(null)
  const [done, setDone] = useState<"approved" | "denied" | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  async function lookUp(event?: FormEvent<HTMLFormElement>) {
    event?.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const res = await api.get<{ request: DeviceRequest }>(`/auth/device/${code.trim().toUpperCase()}`)
      setRequest(res.request)
    } catch (err) {
      setError(err)
      setRequest(null)
    } finally {
      setBusy(false)
    }
  }

  async function decide(decision: "approve" | "deny") {
    setBusy(true)
    setError(null)
    try {
      await api.post(`/auth/device/${request!.userCode}/${decision}`)
      setDone(decision === "approve" ? "approved" : "denied")
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  if (done === "approved") {
    return (
      <>
        <PageHeader title="Terminal connected" />
        <Card className="flex flex-col gap-2 border-success/40 bg-success-soft p-5">
          <p className="text-foreground">You can go back to your terminal — it is already signing in.</p>
          <p className="text-sm text-muted">
            This machine now bills to {company.name}. Revoke it any time under Settings.
          </p>
        </Card>
      </>
    )
  }

  if (done === "denied") {
    return (
      <>
        <PageHeader title="Request denied" />
        <Card className="p-5">
          <p className="text-foreground">Nothing was connected, and no token was created.</p>
        </Card>
      </>
    )
  }

  return (
    <>
      <PageHeader
        title="Connect a terminal"
        subtitle="Approve the machine that is asking to sign in as you."
      />

      <ErrorNote error={error} />

      {!request ? (
        <Card className="p-5">
          <form onSubmit={lookUp} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="code" className="text-sm text-foreground">
                Enter the code shown in your terminal
              </label>
              <input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX"
                autoFocus
                className="rounded-md border border-border bg-surface px-3 py-2 text-center font-mono text-xl tracking-widest text-foreground outline-none placeholder:text-faint focus:border-interactive"
              />
            </div>
            <div>
              <Button type="submit" disabled={busy || code.trim().length < 8}>
                {busy ? "Checking…" : "Continue"}
              </Button>
            </div>
          </form>
        </Card>
      ) : (
        <Card className="flex flex-col gap-5 p-5">
          {/* Shown large and on its own. Whoever is reading this has to be able
              to compare it against their terminal without hunting for it. */}
          <div className="flex flex-col items-center gap-1 rounded-md bg-surface-muted py-5">
            <span className="text-xs text-muted">Code in your terminal</span>
            <span className="font-mono text-3xl font-semibold tracking-widest text-foreground">
              {request.userCode}
            </span>
          </div>

          <div className="flex flex-col gap-1 text-sm">
            <p className="text-foreground">
              Sign in as <span className="font-medium">{employee.email}</span>
            </p>
            <p className="text-muted">
              {request.label ? `Asked for by: ${request.label}` : "A terminal on this account"}
            </p>
            <p className="text-xs text-faint">Requested {formatDateTime(request.createdAt)}</p>
          </div>

          <p className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
            Only approve this if that code matches what your own terminal is showing. Approving
            gives that machine full access to this account, and everything it spends is billed to{" "}
            {company.name}.
          </p>

          <div className="flex gap-2">
            <Button onClick={() => decide("approve")} disabled={busy}>
              {busy ? "Connecting…" : "Approve"}
            </Button>
            <Button variant="ghost" onClick={() => decide("deny")} disabled={busy}>
              This is not me
            </Button>
          </div>
        </Card>
      )}

      {busy && !request && <Spinner />}
    </>
  )
}
